<div
  class="cell-container"
  [class.cell--regular]="config().size === 'regular'"
  [class.cell--body]="config().size === 'body'"
  [class.cell--disabled]="disabled()"
  [class.cell--regular-emphasis]="config().emphasis === 'regular'"
  [attr.role]="getCellRole()"
  [attr.aria-label]="getAriaLabel()"
>
  <!-- Informative Cells -->
  @if (isInformativeCell()) {
    <div class="cell-content cell-content--informative">
      <!-- Single Line -->
      @if (config().variant === 'single-line') {
        <div
          class="cell-single-line"
          [class.cell-single-line--inline]="
            config().chevronPosition === 'inline'
          "
        >
          <span class="cell-text cell-text--primary">{{
            config().text || config().primaryText
          }}</span>
          @if (config().showChevron) {
            <v-one-icon
              [name]="config().chevronIcon || 'chevronRight'"
              [type]="ICON_TYPE.OUTLINE"
              class="cell-chevron"
              [class.cell-chevron--inline]="
                config().chevronPosition === 'inline'
              "
              [class.cell-chevron--primary]="config().chevronTone === 'primary'"
            />
          }
          @if (config().secondaryText) {
            <span class="cell-text cell-text--primary">{{
              config().secondaryText
            }}</span>
          }
          @if (config().showTag && config().tagText) {
            <v-one-chips-tags
              [label]="config().tagText || ''"
              [type]="'tag'"
              [tagState]="config().tagState || 'default'"
            />
          }
        </div>
      }

      <!-- Multi Line / Double Line -->
      @if (
        config().variant === 'multi-line' || config().variant === 'double-line'
      ) {
        <div class="cell-multi-line">
          <span class="cell-text cell-text--primary">{{
            config().text || config().primaryText
          }}</span>
          <span class="cell-text cell-text--secondary">{{
            config().secondaryText
          }}</span>
        </div>
      }

      <!-- Status -->
      @if (config().variant === 'status') {
        <div class="cell-status">
          <v-one-icon
            [name]="config().statusIcon || 'check'"
            [type]="ICON_TYPE.OUTLINE"
            class="cell-status-icon"
            [class.cell-status-icon--success]="
              (config().status || config().statusType) === 'success'
            "
            [class.cell-status-icon--warning]="
              (config().status || config().statusType) === 'warning'
            "
            [class.cell-status-icon--error]="
              (config().status || config().statusType) === 'error'
            "
            [class.cell-status-icon--info]="
              (config().status || config().statusType) === 'info'
            "
          />
          <span class="cell-text cell-text--primary">{{
            config().statusText || config().text || config().primaryText
          }}</span>
        </div>
      }

      <!-- Tag -->
      @if (config().variant === 'tag') {
        <div class="cell-tag">
          <v-one-chips-tags
            [label]="config().tagText || config().text || ''"
            [type]="'tag'"
            [tagState]="config().tagState || 'default'"
            [showIcon]="config().showTag === true"
            [compact]="true"
          />
        </div>
      }

      <!-- With Remark -->
      @if (config().variant === 'with-remark') {
        <div class="cell-with-remark">
          <span class="cell-text cell-text--primary">{{
            config().text || config().primaryText
          }}</span>
          <span class="cell-text cell-text--description">{{
            config().remark || config().description || config().secondaryText
          }}</span>
        </div>
      }
    </div>
  }

  <!-- Action Cells -->
  @if (isActionCell()) {
    <div class="cell-content cell-content--action">
      <!-- Action / Action + Icon -->
      @if (
        config().variant === 'action' || config().variant === 'action-icon'
      ) {
        <div class="cell-action-wrapper">
          <button
            type="button"
            class="cell-action-button"
            [disabled]="disabled()"
            (click)="handleAction()"
            [attr.aria-label]="
              config().actionLabel || config().actionText || 'Action'
            "
          >
            <span class="cell-text cell-text--action">{{
              config().actionText || config().actionLabel || config().text
            }}</span>
            @if (config().actionIcon) {
              <v-one-icon
                [name]="config().actionIcon || 'arrowRight'"
                [type]="ICON_TYPE.OUTLINE"
                class="cell-action-icon"
              />
            }
          </button>
          @if (config().showTag && config().tagText) {
            <v-one-chips-tags
              [label]="config().tagText || ''"
              [type]="'tag'"
              [tagState]="config().tagState || 'default'"
            />
          }
        </div>
      }

      <!-- Toggle -->
      @if (config().variant === 'toggle') {
        <label class="cell-toggle">
          <input
            type="checkbox"
            class="cell-toggle-input"
            [checked]="toggleValue()"
            [disabled]="disabled()"
            (change)="handleToggle()"
            [attr.aria-label]="config().primaryText || 'Toggle'"
          />
          <span class="cell-toggle-slider" aria-hidden="true"></span>
          <span class="sr-only">{{ config().primaryText }}</span>
        </label>
      }

      <!-- Checkbox -->
      @if (config().variant === 'checkbox') {
        <input
          type="checkbox"
          class="cell-checkbox"
          [checked]="checkboxValue()"
          [disabled]="disabled()"
          (change)="handleCheckbox()"
          [attr.aria-label]="config().primaryText || 'Checkbox'"
        />
      }

      <!-- Icon Only -->
      @if (config().variant === 'icon') {
        <button
          type="button"
          class="cell-icon-button"
          [disabled]="disabled()"
          (click)="handleAction()"
          [attr.aria-label]="config().actionLabel || 'Icon action'"
        >
          <v-one-icon
            [name]="config().icon || 'add'"
            [type]="ICON_TYPE.OUTLINE"
          />
        </button>
      }
    </div>
  }

  <!-- Editable Cells -->
  @if (isEditableCell()) {
    <div class="cell-content cell-content--editable">
      <!-- Dropdown -->
      @if (config().variant === 'dropdown') {
        <select
          class="cell-dropdown"
          [disabled]="disabled()"
          (change)="handleTextChange($any($event.target).value)"
          [attr.aria-label]="config().primaryText || 'Dropdown'"
        >
          <option value="">{{ config().placeholder || 'Select...' }}</option>
        </select>
      }

      <!-- Search -->
      @if (config().variant === 'search') {
        <div class="cell-search">
          <span class="cell-search-icon" aria-hidden="true">
            <v-one-icon [name]="'search'" [type]="ICON_TYPE.OUTLINE" />
          </span>
          <input
            type="search"
            class="cell-search-input"
            [value]="searchValue()"
            [disabled]="disabled()"
            [placeholder]="config().placeholder || 'Search...'"
            (input)="handleSearchChange($any($event.target).value)"
            [attr.aria-label]="config().primaryText || 'Search'"
          />
        </div>
      }

      <!-- Text Input -->
      @if (config().variant === 'text') {
        <input
          type="text"
          class="cell-text-input"
          [value]="textValue()"
          [disabled]="disabled()"
          [placeholder]="config().placeholder || 'Enter text...'"
          (input)="handleTextChange($any($event.target).value)"
          [attr.aria-label]="config().primaryText || 'Text input'"
        />
      }
    </div>
  }
</div>
